import { DEFAULT_PAGE_SIZE, type UnifiedOrderListQuery } from '@flux/shared';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Import } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { RowSelectionState } from '@/components/data-table/data-table.js';
import { DataTable, useDataTableUrlState } from '@/components/data-table/data-table.js';
import { ListToolbar } from '@/components/data-table/list-toolbar.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { ApiError } from '@/lib/api/client.js';
import { useOriginFilter } from '@/lib/origin-filter.js';
import { BulkActionBar } from './bulk-action-bar.js';
import { ORDER_SORTABLE_COLUMN_IDS, orderColumns } from './columns.js';
import { ImportOrderSheet } from './import-order-sheet.js';
import { hasActiveOrderFilters, OrderFilters } from './order-filters.js';
import { readOrderFilters } from './read-order-filters.js';
import { useUnifiedOrdersList } from './use-orders-queries.js';

/**
 * Écran Commandes (`02` §3) — l'écran central de la plateforme depuis la
 * refonte du 10/09/2026 : commandes Shopify **et** factures Pennylane dans la
 * même liste, un seul statut, recherche en premier. Route associée :
 * `routes/commandes.tsx` (`ordersRoute`, `path: '/commandes'`).
 */
export function OrdersPage(): JSX.Element {
  const navigate = useNavigate();
  const rawSearch: unknown = useSearch({ strict: false });
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { origin, setOrigin } = useOriginFilter();
  const filters = readOrderFilters(rawSearch);

  const query: UnifiedOrderListQuery = {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    ...(sortBy ? { sortBy } : {}),
    sortDir,
    ...(origin ? { origin: [origin] } : {}),
    ...filters,
  };

  const ordersQuery = useUnifiedOrdersList(query);
  const items = ordersQuery.data?.items ?? [];

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [importOpen, setImportOpen] = useState(false);

  // Nouvelle page / nouveaux filtres → la sélection précédente ne correspond
  // plus aux lignes affichées (`02` §3, sélection multiple par page).
  const queryFingerprint = JSON.stringify(query);
  useEffect(() => {
    setRowSelection({});
  }, [queryFingerprint]);

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const selectedStatuses = items
    .filter((item) => selectedIds.includes(item.id))
    .flatMap((item) => (item.order ? [item.order.status] : []));

  return (
    <Page>
      <PageHeader
        title="Commandes"
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setImportOpen(true);
            }}
          >
            <Import aria-hidden="true" />
            Importer…
          </Button>
        }
      />

      <DataTable
        columns={orderColumns}
        data={items}
        getRowId={(row) => row.id}
        total={ordersQuery.data?.total ?? 0}
        page={page}
        pageSize={DEFAULT_PAGE_SIZE}
        sortBy={sortBy}
        sortDir={sortDir}
        sortableColumnIds={ORDER_SORTABLE_COLUMN_IDS}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={ordersQuery.isPending}
        isError={ordersQuery.isError}
        {...(ordersQuery.error instanceof ApiError
          ? { errorMessage: ordersQuery.error.message }
          : {})}
        errorDetail={ordersQuery.error instanceof ApiError ? ordersQuery.error.detail : undefined}
        onRetry={() => void ordersQuery.refetch()}
        onRowClick={(row) => {
          void navigate({
            to:
              row.origin.kind === 'pennylane'
                ? `/commandes/factures/${row.id}`
                : `/commandes/${row.id}`,
          });
        }}
        // `02` §3 : les actions groupées ne valent que pour les commandes
        // Shopify — une facture Pennylane n'est pas sélectionnable.
        isRowSelectable={(row) => row.origin.kind === 'shopify'}
        toolbar={
          // Barre d'actions groupées juste au-dessus du tableau, sous la
          // recherche et les filtres : elle ne s'affiche qu'avec une sélection.
          <div className="flex flex-col gap-3">
            <ListToolbar
              search={{
                value: filters.search ?? '',
                onChange: (next) => {
                  setFilters({ search: next === '' ? undefined : next });
                },
                placeholder: 'Numéro de commande ou de facture, client, email, SKU…',
              }}
              isFiltered={hasActiveOrderFilters(filters, origin)}
              onReset={() => {
                setOrigin(null);
                setFilters({
                  status: undefined,
                  from: undefined,
                  to: undefined,
                  operationId: undefined,
                  withWarnings: undefined,
                  alertCode: undefined,
                  alertFrom: undefined,
                  alertTo: undefined,
                });
              }}
            >
              <OrderFilters
                value={filters}
                onChange={setFilters}
                origin={origin}
                onOriginChange={setOrigin}
              />
            </ListToolbar>
            <BulkActionBar
              selectedIds={selectedIds}
              selectedStatuses={selectedStatuses}
              onDone={() => {
                setRowSelection({});
              }}
            />
          </div>
        }
        emptyState={{
          title: 'Aucune commande',
          description: 'Aucune commande ne correspond à ces filtres.',
        }}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
      />

      <ImportOrderSheet
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={(target) => {
          void navigate({ to: target });
        }}
      />
    </Page>
  );
}
